import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

import { environment } from '@environments/environment';
import { toFormData } from '@platform/shared/utilities/file-utility';
import { PostMobileUploadResponseDto } from '@core/mobile-upload/mobile-upload.model';
import { ItemsResponseDto } from '@platform/shared/items-response.dto';
import { InboxDto } from '@apps/fiduciary/cockpit/inbox/inbox.model';
import { ProcessTemplateDto } from './process-template/process-template.model';

import {
  AdditionalFolderDetailDto,
  ApprovalLogDto,
  PatchPositionCollectionStatusDto,
  PatchPositionStatusDto,
  PatchPositionStatusResultDto,
  PatchProcessApprovalDto,
  PatchProcessClientMainContactDto,
  PatchProcessStatusDto,
  PositionDto,
  PositionStatus,
  PostPositionCommentDto,
  PostProcessDto,
  PostProcessUserDto,
  ProcessAdminDto,
  ProcessDto,
  ProcessType,
  PutProcessDto,
  SourceProcessDetailDto,
  SourceProcessDto,
  UpdateAdditionalFolderFileNameDto,
} from './process/process.model';
import { LegalEntityDto, PostLegalEntityDto } from './legal-entity/legal-entity.model';
import { CockpitProcessDto } from './cockpit/cockpit-process.model';
import { ProcessLastDownloadsInfo } from './file-process.state';

@Injectable({
  providedIn: 'root',
})
export class FileProcessService {
  constructor(private http: HttpClient) {}

  public getCockpitProcesses(customerSpaceId: string, processType: ProcessType) {
    return this.http.get<ItemsResponseDto<CockpitProcessDto>>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/processes/recent`,
    );
  }

  public getProcessTemplates(processType: ProcessType): Observable<ProcessTemplateDto[]> {
    return this.http.get<ProcessTemplateDto[]>(`${environment.apiUrl}/file-process/${processType}/templates`);
  }

  public getSourceProcesses(processType: ProcessType, customerSpaceId: string): Observable<SourceProcessDto[]> {
    return this.http.get<SourceProcessDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/processes/source`,
    );
  }

  public getSourceProcessDetail(
    processType: ProcessType,
    customerSpaceId: string,
    sourceProcessId: string,
  ): Observable<SourceProcessDetailDto> {
    return this.http.get<SourceProcessDetailDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/processes/source/${sourceProcessId}`,
    );
  }

  public getLegalEntity(customerSpaceId: string, processType: ProcessType, legalEntityId: string): Observable<LegalEntityDto> {
    return this.http.get<LegalEntityDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}`,
    );
  }

  public getCustomerSpaceLegalEntitiesForList(customerSpaceId: string, processType: ProcessType): Observable<LegalEntityDto[]> {
    return this.http.get<LegalEntityDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities`,
    );
  }

  public createLegalEntity(
    customerSpaceId: string,
    processType: ProcessType,
    postLegalEntityDto: PostLegalEntityDto,
  ): Observable<LegalEntityDto> {
    return this.http.post<LegalEntityDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities`,
      postLegalEntityDto,
    );
  }

  public updateLegalEntity(
    customerSpaceId: string,
    processType: ProcessType,
    legalEntityId: string,
    postLegalEntityDto: PostLegalEntityDto,
  ): Observable<LegalEntityDto> {
    return this.http.put<LegalEntityDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}`,
      postLegalEntityDto,
    );
  }

  public deleteLegalEntity(customerSpaceId: string, processType: ProcessType, legalEntityId: string): Observable<LegalEntityDto[]> {
    return this.http.delete<LegalEntityDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}`,
    );
  }

  public getLegalEntityProcessesForList(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
  ): Observable<ProcessDto[]> {
    return this.http.get<ProcessDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes`,
    );
  }

  public getProcess(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    loadFiles: boolean = true,
  ): Observable<ProcessDto> {
    return this.http.get<ProcessDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}?loadFiles=${loadFiles}`,
    );
  }

  public getProcessLastUserDownloadInfo(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
  ): Observable<ProcessLastDownloadsInfo> {
    return this.http.get<ProcessLastDownloadsInfo>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/last-user-downloads`,
    );
  }

  public createProcess(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    process: PostProcessDto,
  ): Observable<ProcessDto> {
    return this.http.post<ProcessDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes`,
      process,
    );
  }

  public deleteProcess(customerSpaceId: string, legalEntityId: string, processType: ProcessType, processId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}`,
    );
  }

  public getProcessAdmin(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
  ): Observable<ProcessAdminDto> {
    return this.http.get<ProcessAdminDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/admin`,
    );
  }

  public updateProcess(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    putProcessDto: PutProcessDto,
  ): Observable<ProcessAdminDto> {
    return this.http.put<ProcessAdminDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}`,
      putProcessDto,
    );
  }

  public updateProcessStatus(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    patchProcessStatusDto: PatchProcessStatusDto,
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/status`,
      patchProcessStatusDto,
    );
  }

  public updateProcessApproval(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    patchProcessApprovalDto: PatchProcessApprovalDto,
  ): Observable<ApprovalLogDto[]> {
    return this.http.patch<ApprovalLogDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/approval`,
      patchProcessApprovalDto,
    );
  }

  public updateProcessClientMainContact(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    patchDto: PatchProcessClientMainContactDto,
  ): Observable<void> {
    const url = `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/client-main-contact`;
    return this.http.patch<void>(url, patchDto).pipe(shareReplay());
  }

  public updatePositionsCollectionStatus(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    positionIds: string[],
    status: PositionStatus,
    sendMail: boolean,
  ): Observable<PatchPositionStatusResultDto> {
    return this.http.patch<PatchPositionStatusResultDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/positionCollections/status`,
      {
        status,
        sendMail,
        positionIds,
      } as PatchPositionCollectionStatusDto,
    );
  }

  public updatePositionStatus(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
    patchPositionStatusDto: PatchPositionStatusDto,
  ): Observable<PatchPositionStatusResultDto> {
    return this.http.patch<PatchPositionStatusResultDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/status`,
      patchPositionStatusDto,
    );
  }

  public addPositionComment(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
    postPositionCommentDto: PostPositionCommentDto,
  ): Observable<PositionDto> {
    return this.http.post<PositionDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/comments`,
      postPositionCommentDto,
    );
  }

  public deletePositionComment(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
    commentId: string,
  ): Observable<PositionDto> {
    return this.http.delete<PositionDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/comments/${commentId}`,
    );
  }

  public addProcessUser(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    postProcessUserDto: PostProcessUserDto,
  ): Observable<ProcessAdminDto> {
    return this.http.post<ProcessAdminDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/users`,
      postProcessUserDto,
    );
  }

  public deleteProcessUser(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    userId: string,
  ): Observable<ProcessAdminDto> {
    return this.http.delete<ProcessAdminDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/users/${userId}`,
    );
  }

  public downloadAllProcessFiles(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
  ): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/download-all`,
      { responseType: 'blob' },
    );
  }

  public downloadNewProcessFiles(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    isMyLast: boolean,
  ): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/download-new`,
      { responseType: 'blob', params: { isMyLast } },
    );
  }

  public uploadFilesToPosition(
    files: FileList,
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
  ): Observable<PositionDto> {
    const url = `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/files`;

    const formData = toFormData(files);
    return this.http.post<PositionDto>(url, formData);
  }

  public createMobileUploadForPosition(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
  ): Observable<PostMobileUploadResponseDto> {
    return this.http.post<PostMobileUploadResponseDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/mobile-upload`,
      {},
    );
  }

  public uploadFilesToProcess(
    files: FileList,
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
  ): Observable<ProcessDto> {
    const url = `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/files`;

    const formData = toFormData(files);
    return this.http.post<ProcessDto>(url, formData);
  }

  public downloadFileFromPosition(
    fileName: string,
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId?: string,
    positionId?: string,
  ): Observable<Blob> {
    const url = `${
      environment.apiUrl
    }/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/files/${encodeURIComponent(
      fileName,
    )}`;

    return this.http.get(url, {
      responseType: 'blob',
    });
  }

  public downloadFileFromProcess(
    fileName: string,
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
  ): Observable<Blob> {
    const url = `${
      environment.apiUrl
    }/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/files/${encodeURIComponent(
      fileName,
    )}`;

    return this.http.get(url, {
      responseType: 'blob',
    });
  }

  public deleteFileFromPosition(
    fileName: string,
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
  ): Observable<PositionDto> {
    const url = `${
      environment.apiUrl
    }/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}/files/${encodeURIComponent(
      fileName,
    )}`;

    return this.http.delete<PositionDto>(url);
  }

  public deleteFileFromProcess(
    fileName: string,
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
  ): Observable<ProcessDto> {
    const url = `${
      environment.apiUrl
    }/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/files/${encodeURIComponent(
      fileName,
    )}`;

    return this.http.delete<ProcessDto>(url);
  }

  public loadPosition(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    sectionId: string,
    positionId: string,
  ): Observable<PositionDto> {
    const url = `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/sections/${sectionId}/positions/${positionId}`;
    return this.http.get<PositionDto>(url);
  }

  public getAdditionalFolderDetail(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    folderName: string,
  ): Observable<AdditionalFolderDetailDto> {
    const url = `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/additional-folders/${folderName}`;
    return this.http.get<AdditionalFolderDetailDto>(url);
  }

  public downloadAdditionalFolderFiles(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    folderName: string,
    fileNames: string[],
  ): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/additional-folders/${folderName}/download`,
      {
        params: {
          fileName: fileNames,
        },
        responseType: 'blob',
      },
    );
  }

  public uploadAdditionalFolderFiles(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    folderName: string,
    files: FileList | File[],
  ): Observable<AdditionalFolderDetailDto> {
    const formData = toFormData(files);
    return this.http.post<AdditionalFolderDetailDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/additional-folders/${folderName}/upload`,
      formData,
    );
  }

  public createMobileUploadForAdditionalFolder(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    folderName: string,
  ): Observable<PostMobileUploadResponseDto> {
    return this.http.post<PostMobileUploadResponseDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/additional-folders/${folderName}/mobile-upload`,
      {},
    );
  }

  public createMobileUploadForInbox(customerSpaceId: string, inbox: InboxDto) {
    return this.http.post<PostMobileUploadResponseDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/inboxes/${inbox}/mobile-upload`,
      {},
    );
  }

  public renameAdditionalFolderFile(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    folderName: string,
    originalFileName: string,
    newFileName: string,
  ): Observable<AdditionalFolderDetailDto> {
    const request: UpdateAdditionalFolderFileNameDto = {
      originalFileName,
      newFileName,
    };
    return this.http.patch<AdditionalFolderDetailDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/additional-folders/${folderName}/rename`,
      request,
    );
  }

  public deleteAdditionalFolderFiles(
    customerSpaceId: string,
    legalEntityId: string,
    processType: ProcessType,
    processId: string,
    folderName: string,
    fileNames: string[],
  ): Observable<AdditionalFolderDetailDto> {
    return this.http.delete<AdditionalFolderDetailDto>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/file-process/${processType}/legal-entities/${legalEntityId}/processes/${processId}/additional-folders/${folderName}/delete`,
      {
        params: {
          fileName: fileNames,
        },
      },
    );
  }
}
